// src/pages/TrialSignup.js
// Public /trial form. A parent enters their details ONCE here; on submit we
// create the Firebase Auth account client-side (existing CreateAccount.js
// pattern), then call the authenticated createTrialSignup callable which writes
// the user doc + one children[] entry (course: null) + one trials doc per
// student. Success -> /trial-status.
//
// Existing email: createUserWithEmailAndPassword throws auth/email-already-in-use
// BEFORE the callable. We stash the callable payload and send the parent to
// /login; the resume-after-login wiring lives in Login.js (a later stage).
//
// Feature-flagged: this route is only registered when TRIAL_SIGNUP_ENABLED.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Crown, Sparkles, Shield, User, Users, Plus, X, Eye, EyeOff, XCircle, CheckCircle } from 'lucide-react';
import Footer from '../components/Footer';
import ProfileContactFields from '../components/ProfileContactFields';
import { hasSubdivisions } from '../config/locationData';
import { TRIAL_DIALECTS, HEARD_ABOUT_OPTIONS } from '../utils/trialHelper';
import { captureAttribution, getAttribution, clearAttribution } from '../utils/attribution';
import { stashPendingTrialSignup, clearPendingTrialSignup } from '../utils/pendingTrialSignup';
import { validatePassword } from '../utils/passwordValidation';

// ─── Validation helpers (mirror CreateAccount.js) ────────────────────────────

const validateEmail = (email) => {
  const re = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  return re.test(email) ? '' : 'Please enter a valid email address';
};

// age from a 'YYYY-MM-DD' string
const ageFrom = (dobStr) => {
  if (!dobStr) return null;
  const birth = new Date(dobStr);
  if (isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
};

const getPasswordStrength = (password) => {
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) score++;
  const map = [
    { strength: 'Very Weak', color: 'bg-red-500', width: '10%' },
    { strength: 'Weak', color: 'bg-red-400', width: '25%' },
    { strength: 'Fair', color: 'bg-yellow-400', width: '45%' },
    { strength: 'Good', color: 'bg-yellow-500', width: '65%' },
    { strength: 'Strong', color: 'bg-green-400', width: '85%' },
    { strength: 'Very Strong', color: 'bg-green-500', width: '100%' },
  ];
  return map[score];
};

const TODAY = new Date().toISOString().split('T')[0];

function TrialSignup() {
  const navigate = useNavigate();

  // The public trial form is for logged-OUT visitors: it creates an Auth account
  // on submit, which would replace an existing session. If someone is already
  // signed in, send them into the app. (Stage 5 note: once /trial-status exists
  // and is viewable by any signed-in user, route there instead.)
  useEffect(() => {
    if (auth.currentUser) navigate('/', { replace: true });
  }, [navigate]);

  // Capture fbclid / referrer / landingPath / ?ref= for a direct /trial hit.
  useEffect(() => { captureAttribution(); }, []);

  // Account type
  const [isAdult, setIsAdult] = useState(false);

  // Parent / account holder
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [contact, setContact] = useState({
    phoneNumber: '', country: '', stateProvince: '', city: '',
  });

  // Students — for child mode. Adult mode uses a single implicit student.
  const [students, setStudents] = useState([{ name: '', dob: '' }]);
  const [adultDob, setAdultDob] = useState('');

  // Learning preferences
  const [dialect, setDialect] = useState('');
  const [arabicLevel, setArabicLevel] = useState('');
  const [preferredTimes, setPreferredTimes] = useState('');

  // Attribution extras
  const [heardAbout, setHeardAbout] = useState('');
  const [heardAboutOther, setHeardAboutOther] = useState('');

  // Policies
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [agreedPrivacy, setAgreedPrivacy] = useState(false);

  // Submit state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  // Set when the Auth account was created but the callable failed — lets the
  // parent retry the callable without re-creating the account.
  const [retryPayload, setRetryPayload] = useState(null);

  const setContactField = (partial) => setContact((c) => ({ ...c, ...partial }));

  const addStudent = () => {
    if (students.length < 5) setStudents((s) => [...s, { name: '', dob: '' }]);
  };
  const removeStudent = (i) => {
    if (students.length > 1) setStudents((s) => s.filter((_, idx) => idx !== i));
  };
  const updateStudent = (i, patch) =>
    setStudents((s) => s.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));

  // ─── Build the createTrialSignup payload ──────────────────────────────────
  const buildPayload = () => {
    const composedName = `${firstName.trim()} ${lastName.trim()}`.trim();
    const attribution = { ...getAttribution(), heardAbout: heardAbout || null };
    if (heardAbout === 'Other') attribution.heardAboutOther = heardAboutOther.trim() || null;

    const payloadStudents = isAdult
      ? [{ name: composedName, dateOfBirth: adultDob || null }]
      : students
          .filter((s) => s.name.trim())
          .map((s) => ({ name: s.name.trim(), dateOfBirth: s.dob || null }));

    return {
      isAdult,
      acceptedPolicies: true,
      parent: {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: contact.phoneNumber.trim(),
      },
      students: payloadStudents,
      intake: {
        country: contact.country.trim(),
        stateProvince: contact.stateProvince.trim(),
        city: contact.city.trim(),
        dialect: dialect || null,
        arabicLevel: arabicLevel.trim() || null,
        preferredTimes: preferredTimes.trim() || null,
      },
      attribution,
    };
  };

  // ─── Validation ──────────────────────────────────────────────────────────
  const validate = () => {
    const fe = {};
    if (!firstName.trim()) fe.firstName = 'Required';
    if (!lastName.trim()) fe.lastName = 'Required';

    const emailErr = validateEmail(email);
    if (emailErr) fe.email = emailErr;

    const pwErr = validatePassword(password);
    if (pwErr) fe.password = pwErr;
    if (password !== confirmPassword) fe.confirmPassword = 'Passwords do not match';

    if (!contact.phoneNumber.trim()) fe.phoneNumber = 'Phone number is required';
    if (!contact.country.trim()) fe.country = 'Please select your country';
    if (hasSubdivisions(contact.country) && !contact.stateProvince.trim()) {
      fe.stateProvince = 'Please select your state/province';
    }

    if (isAdult) {
      if (!adultDob) fe.adultDob = 'Please enter your date of birth';
      else if ((ageFrom(adultDob) ?? 99) < 18) fe.adultDob = 'You must be at least 18 to enrol as an adult learner';
    } else {
      const named = students.filter((s) => s.name.trim());
      if (named.length === 0) fe.students = 'Add at least one child';
      // DOB is required, but ONLY for a row that actually has a name — the
      // form allows up to 5 rows and silently drops blank ones at submit, so
      // an untouched fifth row must never block submit with an error the
      // parent can't see the cause of.
      students.forEach((s, i) => {
        if (!s.name.trim()) return;
        if (!s.dob) {
          fe[`student_${i}`] = `Please enter ${s.name.trim()}'s date of birth`;
        } else {
          const age = ageFrom(s.dob);
          if (age !== null && age < 3) fe[`student_${i}`] = `${s.name.trim()} must be at least 3 years old`;
        }
      });
    }

    if (heardAbout === 'Other' && !heardAboutOther.trim()) fe.heardAboutOther = 'Please tell us how you heard about us';

    if (!agreedTerms || !agreedPrivacy) fe.policies = 'Please accept the Terms of Service and Privacy Policy';

    return fe;
  };

  // ─── Submit ──────────────────────────────────────────────────────────────
  const callCreateTrialSignup = async (payload) => {
    const fns = getFunctions();
    const res = await httpsCallable(fns, 'createTrialSignup')(payload);
    clearAttribution();
    clearPendingTrialSignup();
    navigate('/trial-status', { replace: true });
    return res;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const fe = validate();
    setFieldErrors(fe);
    if (Object.keys(fe).length > 0) {
      setError('Please fix the highlighted fields.');
      return;
    }

    setSubmitting(true);
    const payload = buildPayload();

    try {
      const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
      try {
        await updateProfile(cred.user, { displayName: `${firstName.trim()} ${lastName.trim()}` });
      } catch { /* non-fatal */ }

      try {
        await callCreateTrialSignup(payload);
      } catch (callErr) {
        // Account exists now; let the parent retry the callable only.
        console.error('createTrialSignup failed:', callErr);
        setRetryPayload(payload);
        setError('Your account was created, but we could not submit your trial request. Please try again.');
      }
    } catch (authErr) {
      if (authErr.code === 'auth/email-already-in-use') {
        // Stash the payload (keyed to this email) and hand off to /login.
        // Login resumes it after sign-in; on failure /trial-status retries it.
        stashPendingTrialSignup(email.trim(), payload);
        navigate('/login', { state: { fromTrialSignup: true } });
        return;
      }
      if (authErr.code === 'auth/weak-password') setError('Please choose a stronger password.');
      else if (authErr.code === 'auth/invalid-email') setError('Please enter a valid email address.');
      else {
        console.error('Trial signup auth error:', authErr);
        setError('Something went wrong creating your account. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetry = async () => {
    if (!retryPayload) return;
    setSubmitting(true);
    setError(null);
    try {
      await callCreateTrialSignup(retryPayload);
    } catch (err) {
      console.error('createTrialSignup retry failed:', err);
      setError('Still could not submit your trial request. Please contact us and we will sort it out.');
    } finally {
      setSubmitting(false);
    }
  };

  const strength = password ? getPasswordStrength(password) : null;
  const inputClass =
    'w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white';

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 mb-6">
            <Sparkles className="text-yellow-400" size={22} />
            <span className="text-white font-bold text-lg">Book a Free Trial Class</span>
            <Sparkles className="text-yellow-400" size={22} />
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-3 drop-shadow-2xl">
            Try My Roots, on us
          </h1>
          <p className="text-lg text-purple-100 max-w-2xl mx-auto">
            Tell us a little about {isAdult ? 'yourself' : 'your child'} and we will set up a free trial
            class with one of our teachers.
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl mb-8">
            {retryPayload ? (
              <div className="text-center py-6">
                <XCircle className="text-orange-500 mx-auto mb-4" size={40} />
                <h2 className="text-xl font-bold text-gray-800 mb-2">Almost there</h2>
                <p className="text-gray-600 mb-6">{error}</p>
                <button
                  onClick={handleRetry}
                  disabled={submitting}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-3 px-8 rounded-xl transition-all disabled:opacity-50"
                >
                  {submitting ? 'Submitting…' : 'Submit trial request'}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {/* Account type */}
                <div className="mb-8 p-5 bg-blue-50 rounded-2xl border border-blue-200">
                  <div className="text-sm font-semibold text-gray-700 mb-3">Who is the trial class for?</div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setIsAdult(false)}
                      className={`flex-1 py-3 px-4 rounded-xl border-2 font-semibold text-sm transition-all ${
                        !isAdult ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-300 bg-white text-gray-600'
                      }`}
                    >
                      My child(ren)
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsAdult(true)}
                      className={`flex-1 py-3 px-4 rounded-xl border-2 font-semibold text-sm transition-all ${
                        isAdult ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-300 bg-white text-gray-600'
                      }`}
                    >
                      Myself (adult learner)
                    </button>
                  </div>
                </div>

                {/* Parent / account holder */}
                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                    <Shield className="text-purple-600" size={24} />
                    {isAdult ? 'Your Information' : 'Parent Information'}
                  </h3>

                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">First Name</label>
                      <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} className={inputClass} required />
                      {fieldErrors.firstName && <p className="text-red-600 text-sm mt-1">{fieldErrors.firstName}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name</label>
                      <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} className={inputClass} required />
                      {fieldErrors.lastName && <p className="text-red-600 text-sm mt-1">{fieldErrors.lastName}</p>}
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} required />
                    {fieldErrors.email && <p className="text-red-600 text-sm mt-1">{fieldErrors.email}</p>}
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className={`${inputClass} pr-12`}
                          required
                        />
                        <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                          {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                      </div>
                      {strength && (
                        <div className="mt-2">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className={`h-2 rounded-full transition-all ${strength.color}`} style={{ width: strength.width }} />
                          </div>
                          <div className="text-xs text-gray-500 mt-1">{strength.strength}</div>
                        </div>
                      )}
                      {fieldErrors.password && <p className="text-red-600 text-sm mt-1">{fieldErrors.password}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Confirm Password</label>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className={inputClass}
                        required
                      />
                      {confirmPassword && !fieldErrors.confirmPassword && password === confirmPassword && (
                        <p className="text-green-600 text-sm mt-1 flex items-center gap-1"><CheckCircle size={14} /> Passwords match</p>
                      )}
                      {fieldErrors.confirmPassword && <p className="text-red-600 text-sm mt-1">{fieldErrors.confirmPassword}</p>}
                    </div>
                  </div>

                  <div className="mt-4">
                    <ProfileContactFields values={contact} onChange={setContactField} errors={fieldErrors} />
                  </div>
                </div>

                {/* Student(s) */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                      <Sparkles className="text-purple-600" size={24} />
                      {isAdult ? 'About You' : 'About Your Child(ren)'}
                    </h3>
                    {!isAdult && students.length < 5 && (
                      <button type="button" onClick={addStudent} className="flex items-center gap-2 px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 font-semibold rounded-xl text-sm">
                        <Plus size={16} /> Add Another Child
                      </button>
                    )}
                  </div>

                  {fieldErrors.students && <p className="text-red-600 text-sm mb-3">{fieldErrors.students}</p>}

                  {isAdult ? (
                    <div className="p-5 bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl border-2 border-purple-200">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Your Date of Birth</label>
                      <input type="date" max={TODAY} value={adultDob} onChange={(e) => setAdultDob(e.target.value)} className={inputClass} />
                      {fieldErrors.adultDob && <p className="text-red-600 text-sm mt-1">{fieldErrors.adultDob}</p>}
                    </div>
                  ) : (
                    students.map((s, i) => (
                      <div key={i} className="mb-4 p-5 bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl border-2 border-purple-200 relative">
                        {students.length > 1 && (
                          <button type="button" onClick={() => removeStudent(i)} className="absolute top-3 right-3 p-2 text-red-500 hover:bg-red-100 rounded-full">
                            <X size={18} />
                          </button>
                        )}
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Child's Full Name</label>
                            <input type="text" value={s.name} onChange={(e) => updateStudent(i, { name: e.target.value })} className={inputClass} />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Date of Birth
                            </label>
                            <input type="date" max={TODAY} value={s.dob} onChange={(e) => updateStudent(i, { dob: e.target.value })} className={inputClass} />
                          </div>
                        </div>
                        {fieldErrors[`student_${i}`] && <p className="text-red-600 text-sm mt-1">{fieldErrors[`student_${i}`]}</p>}
                      </div>
                    ))
                  )}
                </div>

                {/* Learning preferences */}
                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                    {isAdult ? <User className="text-purple-600" size={24} /> : <Users className="text-purple-600" size={24} />}
                    Learning Preferences
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Dialect</label>
                      <select value={dialect} onChange={(e) => setDialect(e.target.value)} className={inputClass}>
                        <option value="">Not sure</option>
                        {TRIAL_DIALECTS.map((d) => (
                          <option key={d.value} value={d.value}>{d.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Arabic Level <span className="text-xs font-normal text-gray-400">(optional)</span>
                      </label>
                      <input type="text" value={arabicLevel} onChange={(e) => setArabicLevel(e.target.value)} placeholder="e.g. Complete beginner" className={inputClass} />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Preferred Times <span className="text-xs font-normal text-gray-400">(optional)</span>
                    </label>
                    <input type="text" value={preferredTimes} onChange={(e) => setPreferredTimes(e.target.value)} placeholder="e.g. Weekday evenings, weekend mornings" className={inputClass} />
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">How did you hear about us?</label>
                    <select value={heardAbout} onChange={(e) => setHeardAbout(e.target.value)} className={inputClass}>
                      <option value="">Prefer not to say</option>
                      {HEARD_ABOUT_OPTIONS.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                    {heardAbout === 'Other' && (
                      <input
                        type="text"
                        value={heardAboutOther}
                        onChange={(e) => setHeardAboutOther(e.target.value)}
                        placeholder="Please tell us"
                        className={`${inputClass} mt-2`}
                      />
                    )}
                    {fieldErrors.heardAboutOther && <p className="text-red-600 text-sm mt-1">{fieldErrors.heardAboutOther}</p>}
                  </div>
                </div>

                {/* Policies */}
                <div className="mb-6 space-y-3">
                  <label className="flex items-start gap-3 text-sm text-gray-700">
                    <input type="checkbox" checked={agreedTerms} onChange={(e) => setAgreedTerms(e.target.checked)} className="mt-1" />
                    <span>
                      I agree to the{' '}
                      <button type="button" onClick={() => window.open('/terms-of-service', '_blank')} className="text-purple-600 hover:text-purple-800 underline font-medium">
                        Terms of Service
                      </button>
                    </span>
                  </label>
                  <label className="flex items-start gap-3 text-sm text-gray-700">
                    <input type="checkbox" checked={agreedPrivacy} onChange={(e) => setAgreedPrivacy(e.target.checked)} className="mt-1" />
                    <span>
                      I agree to the{' '}
                      <button type="button" onClick={() => window.open('/privacy-policy', '_blank')} className="text-purple-600 hover:text-purple-800 underline font-medium">
                        Privacy Policy
                      </button>
                    </span>
                  </label>
                  {fieldErrors.policies && <p className="text-red-600 text-sm">{fieldErrors.policies}</p>}
                </div>

                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                    <div className="text-red-700 text-sm font-medium">{error}</div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-4 px-6 rounded-xl transition-all transform hover:scale-105 disabled:opacity-50 disabled:transform-none shadow-lg"
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Submitting…
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Crown size={20} /> Book My Free Trial
                    </span>
                  )}
                </button>

                <p className="text-center text-sm text-gray-500 mt-4">
                  Already have an account?{' '}
                  <button type="button" onClick={() => navigate('/login')} className="text-purple-600 hover:text-purple-800 underline font-medium">
                    Sign in
                  </button>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
      <Footer variant="minimal" />
    </div>
  );
}

export default TrialSignup;
